/**
 * Menu 5 · Chats (Realtime Chat-Hub) — donkere split-screen.
 *   Links de conversatielijst met twee harde tabs, rechts het actieve chatvenster.
 *   Tab A Cursisten: 1-op-1 met medewerkers en professionals op echte naam + e-mail
 *         (+ werkgever resp. discipline), plus de groepschats per klas.
 *         TRAINER ziet alleen cursisten uit eigen klassen.
 *   Tab B Bedrijven & Opdrachtgevers: threads met HR-contactpersonen uit de B2B offerte-aanvragen.
 *   Belagom support: knop onderin de lijst.
 *
 * Dieplinks: ?contact=<userId> opent direct de thread met die persoon (Roster → [Start Chat],
 * Signaal B2B → [Open Toelichtings-Chat]); ?conv=<id> opent een bestaand gesprek (groepschat).
 */
import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { MessageSquare, Send, Users, Shield, Building2, MessagesSquare, Smartphone, BadgeCheck, Search, ArrowLeft } from 'lucide-react';
import { chatAPI, Conversation, Message } from '../services/chatService';
import { getZakelijkeContacten, ZakelijkContact } from '../services/opleiderCockpitApi';
import { useAuthStore } from '../store/authStore';
import { useOpleiderRol } from '../components/opleiderCockpit/useOpleiderRol';

export type ChatTab = 'cursisten' | 'zakelijk';

interface CursistContact {
  id: string;
  soort: 'medewerker' | 'professional';
  first_name: string;
  last_name: string;
  email: string | null;
  organisatie: string | null;
  training_titel: string;
  contact_type: 'opleider_professional';
  last_message?: string | null;
  last_message_at?: string | null;
  unread?: number;
}

interface AdminContact { id: string; first_name: string; last_name: string; email: string; contact_type: 'opleider_belagom' }

type Soort = 'medewerker' | 'professional' | 'bedrijf' | 'groep' | 'belagom';

interface Actief {
  conv: Conversation;
  soort: Soort;
  naam: string;
  sub: string;
  /** Gesprekspartner (1-op-1); afzendernaam in het venster komt hiervandaan, niet uit het pseudoniem. */
  partnerId: string | null;
}

const CURSISTEN_KEY = ['opleider-chat-contacten'];
const ZAKELIJK_KEY = ['opleider-zakelijke-contacten'];

const naamVan = (c: { first_name?: string; last_name?: string }) => `${c.first_name ?? ''} ${c.last_name ?? ''}`.trim();
const initialen = (n: string) => n.split(' ').map((x) => x[0]).filter(Boolean).slice(0, 2).join('').toUpperCase();

const BADGE: Record<Soort, { cls: string; Icon: React.ComponentType<{ className?: string }> }> = {
  medewerker: { cls: 'bg-emerald-500/15 text-emerald-300 ring-emerald-400/30', Icon: Smartphone },
  professional: { cls: 'bg-violet-500/15 text-violet-300 ring-violet-400/30', Icon: BadgeCheck },
  bedrijf: { cls: 'bg-sky-500/15 text-sky-300 ring-sky-400/30', Icon: Building2 },
  groep: { cls: 'bg-indigo-500/15 text-indigo-300 ring-indigo-400/30', Icon: MessagesSquare },
  belagom: { cls: 'bg-amber-500/15 text-amber-300 ring-amber-400/30', Icon: Shield },
};

const Badge: React.FC<{ soort: Soort; label: string }> = ({ soort, label }) => {
  const { cls, Icon } = BADGE[soort];
  return (
    <span className={`inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide ring-1 ${cls}`}>
      <Icon className="w-3 h-3" />{label}
    </span>
  );
};

function tijdLabel(iso: string | null | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  const vandaag = new Date().toDateString() === d.toDateString();
  return vandaag ? d.toLocaleTimeString('nl-NL', { hour: '2-digit', minute: '2-digit' }) : d.toLocaleDateString('nl-NL', { day: 'numeric', month: 'short' });
}

export const OpleiderChatPage: React.FC<{ tab: ChatTab }> = ({ tab }) => {
  const { t } = useTranslation('pages');
  const { user } = useAuthStore();
  const rol = useOpleiderRol();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [params, setParams] = useSearchParams();
  const [actief, setActief] = useState<Actief | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [zoek, setZoek] = useState('');
  const [sending, setSending] = useState(false);
  const [fout, setFout] = useState<string | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const magZakelijk = rol !== 'TRAINER';

  const contacten = useQuery({
    queryKey: CURSISTEN_KEY,
    queryFn: chatAPI.getOpleiderContacts as () => Promise<{ professionals: CursistContact[]; admins: AdminContact[] }>,
    refetchInterval: 15_000,
  });
  const zakelijk = useQuery({ queryKey: ZAKELIJK_KEY, queryFn: getZakelijkeContacten, enabled: tab === 'zakelijk' && magZakelijk, refetchInterval: 15_000 });
  const gesprekken = useQuery({ queryKey: ['conversations'], queryFn: chatAPI.getConversations, refetchInterval: 30_000 });
  const groepschats = (gesprekken.data ?? []).filter((c) => c.type === 'opleider_groep');

  const cursisten = useMemo(() => contacten.data?.professionals ?? [], [contacten.data]);
  const admins = contacten.data?.admins ?? [];
  // Echte namen voor afzenders in groepschats (users.first_name/last_name is bij medewerkers het pseudoniem).
  const naamPerUser = useMemo(() => new Map(cursisten.map((c) => [c.id, naamVan(c)])), [cursisten]);

  const fetchMessages = async (convId: string) => {
    try {
      const msgs = await chatAPI.getMessages(convId);
      setMessages(msgs);
      setTimeout(() => messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 50);
    } catch (e) {
      console.error('Error loading messages', e);
    }
  };

  const actiefConvId = actief?.conv.id;
  useEffect(() => {
    if (!actiefConvId) return;
    fetchMessages(actiefConvId);
    const interval = setInterval(() => fetchMessages(actiefConvId), 3000);
    return () => clearInterval(interval);
    // fetchMessages gebruikt alleen setters en refs.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [actiefConvId]);

  const cursistSub = (c: CursistContact) => [c.email, c.organisatie ? `(${c.organisatie})` : null].filter(Boolean).join(' ');
  const bedrijfNaam = (c: ZakelijkContact) => [c.bedrijfsnaam, naamVan(c)].filter(Boolean).join(' – ') + (c.functie ? ` (${c.functie})` : '');

  const openCursist = async (c: CursistContact) => {
    setFout(null);
    try {
      const conv = await chatAPI.getOrCreateOpleiderConversation(c.id, 'opleider_professional');
      setActief({ conv, soort: c.soort, naam: naamVan(c) || t('opleiderChat.onbekend'), sub: cursistSub(c), partnerId: c.id });
      setMessages([]);
    } catch (e) {
      console.error('Error opening conversation', e);
      setFout(t('opleiderChat.openFout'));
    }
  };

  const openBedrijf = async (c: ZakelijkContact) => {
    setFout(null);
    try {
      const conv = await chatAPI.getOrCreateOpleiderConversation(c.id, 'opleider_employer');
      setActief({ conv, soort: 'bedrijf', naam: bedrijfNaam(c), sub: [c.email, c.training_titel].filter(Boolean).join(' · '), partnerId: c.id });
      setMessages([]);
    } catch (e) {
      console.error('Error opening conversation', e);
      setFout(t('opleiderChat.openFout'));
    }
  };

  const openBelagom = async () => {
    const admin = admins[0];
    if (!admin) return;
    setFout(null);
    try {
      const conv = await chatAPI.getOrCreateOpleiderConversation(admin.id, 'opleider_belagom');
      setActief({ conv, soort: 'belagom', naam: 'BeLagom', sub: t('opleiderChat.belagomTeam'), partnerId: null });
      setMessages([]);
    } catch (e) {
      console.error('Error opening conversation', e);
      setFout(t('opleiderChat.openFout'));
    }
  };

  const openGroep = (c: Conversation) => {
    setActief({ conv: c, soort: 'groep', naam: c.onderwerp || c.group_name || t('opleiderChat.groepschat'), sub: t('opleiderChat.groepschatSub'), partnerId: null });
    setMessages([]);
  };

  // Dieplinks: ?contact=<userId> (Roster [Start Chat] / Signaal [Open Toelichtings-Chat]) en ?conv=<id> (groepschat)
  const contactParam = params.get('contact');
  const convParam = params.get('conv');
  useEffect(() => {
    if (!contactParam) return;
    if (tab === 'cursisten' && contacten.data) {
      const c = contacten.data.professionals.find((x) => x.id === contactParam);
      if (c) openCursist(c);
      setParams({}, { replace: true });
    } else if (tab === 'zakelijk' && zakelijk.data) {
      const c = zakelijk.data.find((x) => x.id === contactParam);
      if (c) openBedrijf(c);
      setParams({}, { replace: true });
    }
    // open* zijn stabiel genoeg; we reageren alleen op binnenkomende data/param.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contactParam, tab, contacten.data, zakelijk.data]);
  useEffect(() => {
    if (!convParam || !gesprekken.data) return;
    const c = gesprekken.data.find((x) => x.id === convParam);
    if (c) openGroep(c);
    setParams({}, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [convParam, gesprekken.data]);

  // Tab-wissel = ander menu-pad; open gesprek sluit.
  useEffect(() => { setActief(null); setMessages([]); setZoek(''); }, [tab]);

  const sendMessage = async () => {
    if (!inputValue.trim() || !actief || sending) return;
    setSending(true);
    try {
      await chatAPI.sendMessage(actief.conv.id, inputValue.trim());
      setInputValue('');
      await fetchMessages(actief.conv.id);
      queryClient.invalidateQueries({ queryKey: tab === 'zakelijk' ? ZAKELIJK_KEY : CURSISTEN_KEY });
    } catch (e) {
      console.error('Error sending message', e);
      setFout(t('opleiderChat.verstuurFout'));
    } finally {
      setSending(false);
    }
  };

  const q = zoek.trim().toLowerCase();
  const past = (...velden: Array<string | null | undefined>) => !q || velden.some((v) => v?.toLowerCase().includes(q));
  const zichtbareCursisten = cursisten.filter((c) => past(naamVan(c), c.email, c.organisatie, c.training_titel));
  const zichtbareBedrijven = (zakelijk.data ?? []).filter((c) => past(naamVan(c), c.email, c.bedrijfsnaam, c.functie, c.training_titel));
  const zichtbareGroepen = groepschats.filter((c) => past(c.onderwerp, c.group_name));
  const laden = tab === 'zakelijk' ? zakelijk.isLoading : contacten.isLoading;

  const tabKnop = (key: ChatTab, Icon: React.ComponentType<{ className?: string }>, label: string) => (
    <button type="button" role="tab" aria-selected={tab === key} onClick={() => tab !== key && navigate(`/opleider/chats/${key}`)}
      className={`flex-1 px-3 py-3 text-sm font-medium transition-colors border-b-2 ${tab === key ? 'text-white border-sky-400 bg-white/5' : 'text-slate-400 border-transparent hover:text-slate-200'}`}>
      <span className="flex items-center justify-center gap-1.5"><Icon className="w-4 h-4" />{label}</span>
    </button>
  );

  const rij = (opts: { key: string; actief: boolean; soort: Soort; badge: string; naam: string; sub: string; preview?: string | null; tijd?: string | null; unread?: number; onClick: () => void }) => (
    <button key={opts.key} type="button" onClick={opts.onClick}
      className={`w-full flex items-start gap-3 px-3 py-3 rounded-lg text-left transition-colors mb-1 ${opts.actief ? 'bg-sky-500/15 ring-1 ring-sky-400/30' : 'hover:bg-white/5'}`}>
      <div className="w-9 h-9 rounded-full bg-white/10 flex items-center justify-center flex-shrink-0 text-xs font-semibold text-slate-200">{initialen(opts.naam) || '?'}</div>
      <div className="min-w-0 flex-1">
        <div className="flex items-center justify-between gap-2">
          <Badge soort={opts.soort} label={opts.badge} />
          {opts.tijd && <span className="text-[11px] text-slate-500 flex-shrink-0">{tijdLabel(opts.tijd)}</span>}
        </div>
        <p className="mt-1 text-sm font-medium text-slate-100 truncate">{opts.naam}</p>
        <p className="text-xs text-slate-400 truncate">{opts.sub}</p>
        {opts.preview && (
          <p className={`mt-0.5 text-xs truncate ${opts.unread ? 'text-slate-200 font-medium' : 'text-slate-500'}`}>{opts.preview}</p>
        )}
      </div>
      {!!opts.unread && <span className="mt-1 min-w-[1.25rem] h-5 px-1 rounded-full bg-sky-500 text-[11px] font-semibold text-white flex items-center justify-center flex-shrink-0" aria-label={t('opleiderChat.ongelezen')}>{opts.unread}</span>}
    </button>
  );

  const leeg = (tekst: string) => <p className="p-6 text-center text-sm text-slate-500">{tekst}</p>;

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
      <p className="text-sm text-gray-500 mb-4">{tab === 'zakelijk' ? t('opleiderChat.zakelijkIntro') : t('opleiderChat.cursistenIntro')}</p>
      {fout && <p className="mb-3 text-sm text-red-600" role="alert">{fout}</p>}
      <div className="rounded-xl border border-white/10 bg-[#0B0F17] [color-scheme:dark] shadow-lg flex overflow-hidden" style={{ height: '640px' }}>
        {/* ── Linkerkolom: tabs + conversatielijst ── */}
        <div className={`w-full md:w-80 border-r border-white/10 bg-[#0F1522] flex-col flex-shrink-0 ${actief ? 'hidden md:flex' : 'flex'}`}>
          <div className="flex border-b border-white/10" role="tablist">
            {tabKnop('cursisten', Users, t('opleiderChat.tabCursisten'))}
            {magZakelijk && tabKnop('zakelijk', Building2, t('opleiderChat.tabBedrijven'))}
          </div>
          <div className="p-2 border-b border-white/10">
            <label className="relative block">
              <Search className="w-4 h-4 text-slate-500 absolute left-2.5 top-1/2 -translate-y-1/2" />
              <span className="sr-only">{t('opleiderChat.zoeken')}</span>
              <input value={zoek} onChange={(e) => setZoek(e.target.value)} placeholder={t('opleiderChat.zoeken')}
                className="w-full rounded-lg bg-white/5 border border-white/10 pl-8 pr-3 py-2 text-sm text-slate-100 placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-sky-400/40" />
            </label>
          </div>
          <div className="flex-1 overflow-y-auto p-2">
            {laden ? (
              <div className="p-6 text-center"><div className="animate-spin rounded-full h-6 w-6 border-b-2 border-sky-400 mx-auto" /></div>
            ) : tab === 'cursisten' ? (
              <>
                {zichtbareGroepen.length > 0 && (
                  <>
                    <p className="px-3 pt-1 pb-1 text-[11px] uppercase tracking-wide text-slate-500">{t('opleiderChat.groepschats')}</p>
                    {zichtbareGroepen.map((c) => rij({
                      key: c.id, actief: actief?.conv.id === c.id, soort: 'groep', badge: t('opleiderChat.badgeGroep'),
                      naam: c.onderwerp || c.group_name || t('opleiderChat.groepschat'), sub: t('opleiderChat.groepschatSub'),
                      preview: c.last_message, tijd: c.last_message_at, unread: c.unread_count, onClick: () => openGroep(c),
                    }))}
                    <p className="px-3 pt-2 pb-1 text-[11px] uppercase tracking-wide text-slate-500">{t('opleiderChat.eenOpEen')}</p>
                  </>
                )}
                {zichtbareCursisten.length === 0 ? leeg(q ? t('opleiderChat.geenResultaten') : t('opleiderChat.noProfessionals')) : zichtbareCursisten.map((c) => rij({
                  key: c.id, actief: actief?.partnerId === c.id && actief.soort !== 'bedrijf', soort: c.soort,
                  badge: c.soort === 'medewerker' ? t('opleiderChat.badgeMedewerker') : t('opleiderChat.badgeProfessional'),
                  naam: naamVan(c) || t('opleiderChat.onbekend'), sub: cursistSub(c),
                  preview: c.last_message, tijd: c.last_message_at, unread: c.unread, onClick: () => openCursist(c),
                }))}
              </>
            ) : zichtbareBedrijven.length === 0 ? leeg(q ? t('opleiderChat.geenResultaten') : t('opleiderChat.noZakelijk')) : zichtbareBedrijven.map((c) => rij({
              key: c.id, actief: actief?.partnerId === c.id && actief.soort === 'bedrijf', soort: 'bedrijf', badge: t('opleiderChat.badgeBedrijf'),
              naam: bedrijfNaam(c), sub: [c.email, c.training_titel].filter(Boolean).join(' · '),
              preview: c.last_message, tijd: c.last_message_at, unread: c.unread, onClick: () => openBedrijf(c),
            }))}
          </div>
          {admins.length > 0 && (
            <button type="button" onClick={openBelagom}
              className={`m-2 flex items-center justify-center gap-2 rounded-lg border border-white/10 px-3 py-2 text-xs font-medium transition-colors ${actief?.soort === 'belagom' ? 'bg-amber-500/15 text-amber-200' : 'text-slate-400 hover:text-slate-200 hover:bg-white/5'}`}>
              <Shield className="w-3.5 h-3.5" />{t('opleiderChat.belagomSupport')}
            </button>
          )}
        </div>

        {/* ── Rechterkolom: actief chatvenster ── */}
        {actief ? (
          <div className="flex-1 flex flex-col min-w-0">
            <div className="px-4 py-3 border-b border-white/10 bg-white/[0.03] flex items-center gap-3">
              <button type="button" onClick={() => setActief(null)} className="md:hidden p-1.5 rounded-lg text-slate-400 hover:bg-white/10" aria-label={t('opleiderChat.terug')}><ArrowLeft className="w-4 h-4" /></button>
              <div className="w-9 h-9 rounded-full bg-white/10 flex items-center justify-center flex-shrink-0 text-xs font-semibold text-slate-200">{initialen(actief.naam)}</div>
              <div className="min-w-0">
                <div className="flex items-center gap-2 min-w-0">
                  <Badge soort={actief.soort} label={t(`opleiderChat.badge${actief.soort.charAt(0).toUpperCase()}${actief.soort.slice(1)}`)} />
                  <p className="font-medium text-slate-100 truncate">{actief.naam}</p>
                </div>
                {actief.sub && <p className="text-xs text-slate-400 truncate">{actief.sub}</p>}
              </div>
            </div>
            <div className="flex-1 overflow-y-auto p-4 space-y-4">
              {messages.length === 0 ? (
                <div className="flex items-center justify-center h-full"><div className="text-center text-slate-500"><MessageSquare className="w-10 h-10 mx-auto mb-2 opacity-30" /><p className="text-sm">{t('opleiderChat.firstMessage')}</p></div></div>
              ) : (
                messages.map((msg) => {
                  const isOwn = msg.sender_id === user?.id;
                  const afzender = actief.partnerId && msg.sender_id === actief.partnerId ? actief.naam : naamPerUser.get(msg.sender_id) || naamVan(msg);
                  return (
                    <div key={msg.id} className={`flex items-end gap-2 ${isOwn ? 'flex-row-reverse' : ''}`}>
                      {!isOwn && <div className="w-7 h-7 rounded-full bg-white/10 flex items-center justify-center flex-shrink-0 text-[10px] font-medium text-slate-300">{initialen(afzender)}</div>}
                      <div className={`max-w-md ${isOwn ? 'items-end' : 'items-start'} flex flex-col`}>
                        {!isOwn && actief.soort === 'groep' && <p className="text-xs text-slate-500 mb-1 ml-1">{afzender}</p>}
                        <div className={`px-3 py-2 rounded-xl text-sm whitespace-pre-wrap break-words ${isOwn ? 'bg-sky-600 text-white rounded-br-none' : 'bg-white/10 text-slate-100 rounded-bl-none'}`}>{msg.content}</div>
                        <p className="text-[11px] text-slate-500 mt-1 mx-1">{new Date(msg.created_at).toLocaleString('nl-NL', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}</p>
                      </div>
                    </div>
                  );
                })
              )}
              <div ref={messagesEndRef} />
            </div>
            <div className="p-3 border-t border-white/10">
              <div className="flex gap-2">
                <input type="text" value={inputValue} onChange={(e) => setInputValue(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && sendMessage()}
                  placeholder={t('opleiderChat.messagePlaceholder')} className="flex-1 px-4 py-2.5 rounded-lg bg-white/5 border border-white/10 text-sm text-slate-100 placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-sky-400/40" />
                <button type="button" onClick={sendMessage} disabled={!inputValue.trim() || sending} className="p-2.5 bg-sky-600 text-white rounded-lg hover:bg-sky-500 transition-colors disabled:opacity-40 flex-shrink-0" aria-label={t('opleiderChat.verstuur')}><Send className="w-5 h-5" /></button>
              </div>
            </div>
          </div>
        ) : (
          <div className="flex-1 hidden md:flex items-center justify-center text-slate-500">
            <div className="text-center"><MessageSquare className="w-14 h-14 mx-auto mb-4 opacity-20" /><p className="text-sm font-medium text-slate-300">{t('opleiderChat.selectContact')}</p><p className="text-xs text-slate-500 mt-1">{t('opleiderChat.selectContactHint')}</p></div>
          </div>
        )}
      </div>
    </div>
  );
};

export default OpleiderChatPage;
